import React from 'react';
import { css } from '@emotion/react';
import { Badge, Radio } from 'antd';
import type { RadioGroupProps, RadioChangeEvent } from 'antd/lib/radio';
import styled from 'types/theme.types';
import Tooltip from 'components/common/tooltip';
import { typeStyle } from 'constants/typography';

export type RadioButtonOpts<T = string> = {
  value: T;
  content: string | React.ReactElement;
  disabled?: boolean;
  ribbonText?: string;
  tooltipId?: string;
};

export interface RadioButtonsProps extends Omit<RadioGroupProps, 'onChange' | 'size'> {
  onChange?: (value: string) => void;
  buttons?: RadioButtonOpts[];
  size?: RadioGroupProps['size'] | 'huge';
  block?: boolean;
}

// Marks the compact segmented (non-huge) group. The styling below scopes to this class so the huge cards
// are never touched. A plain class, not an emotion one, so it emits no CSS of its own.
const SEGMENTED_GROUP_CLASS = 'radio-buttons-segmented';

const RadioButtons: React.FC<RadioButtonsProps> = React.forwardRef<HTMLDivElement, RadioButtonsProps>(
  ({ onChange, buttons, size, children, className, ...props }, ref) => {
    const handleOnChange = (e: RadioChangeEvent) => onChange?.(e.target.value);
    const isHuge = size === 'huge';
    const ButtonComponent = isHuge ? HugeButton : Radio.Button;
    const groupClassName =
      [className, isHuge ? undefined : SEGMENTED_GROUP_CLASS].filter(Boolean).join(' ') || undefined;

    return (
      <RadioGroupContainer ref={ref} onChange={handleOnChange} className={groupClassName} {...props}>
        <Container isHuge={isHuge}>
          {children}
          {buttons?.map(({ value, content, disabled, ribbonText, tooltipId }) => {
            const button = (
              <Tooltip placement="top" titleId={tooltipId!}>
                <ButtonComponent data-e2e={`radio-button-${value}`} key={value} value={value} disabled={!!disabled}>
                  {isHuge ? <HugeButtonContentContainer>{content}</HugeButtonContentContainer> : content}
                </ButtonComponent>
              </Tooltip>
            );
            return ribbonText ? (
              <Badge.Ribbon text={ribbonText} key={value}>
                {button}
              </Badge.Ribbon>
            ) : (
              button
            );
          })}
        </Container>
      </RadioGroupContainer>
    );
  }
);

RadioButtons.displayName = 'RadioButtons';

const RadioGroupContainer = styled(Radio.Group)`
  .ant-radio-button-wrapper::before {
    // making our big-buttons with 'border-radius' without letting this 'before' element to interrupt
    display: none;
  }

  /* Segmented control — the design system's Button Group (Figma node 2128:2157): connected buttons in one
     lime frame with dividers, a lime fill on the selected segment, a pale-lime hover. Uses the dedicated
     component/button-group tokens. Scoped to the ${SEGMENTED_GROUP_CLASS} marker so the huge cards are
     never touched, and compounded with it so it out-weighs antd's solid-buttonStyle rules — the real
     consumer passes buttonStyle="solid", whose checked text is a theme-blind white that otherwise wins in
     dark. */
  &.${SEGMENTED_GROUP_CLASS} {
    .ant-radio-button-wrapper {
      height: 32px;
      display: inline-flex;
      align-items: center;
      padding-inline: var(--padding-md);
      border-color: var(--component-button-group-border);
      /* text-heading, not text-on-action: it matches the spec's near-black label in light but flips with
           the theme, so an unselected segment stays legible on the dark surface in dark mode. */
      color: var(--text-heading);
      ${typeStyle.labelMd}
    }
    .ant-radio-button-wrapper:first-of-type {
      border-start-start-radius: var(--border-radius-100);
      border-end-start-radius: var(--border-radius-100);
    }
    .ant-radio-button-wrapper:last-of-type {
      border-start-end-radius: var(--border-radius-100);
      border-end-end-radius: var(--border-radius-100);
    }
    /* The 1px separators antd draws between connected buttons — hidden above for the huge cards,
         restored here as the lime dividers the spec shows. */
    .ant-radio-button-wrapper:not(:first-of-type)::before {
      display: block;
      background-color: var(--component-button-group-separator);
    }

    /* The hover fill is a pale lime in both light and dark, so the label is fixed black (text-on-action)
         rather than the theme-aware text-heading, which would go white and vanish on it in dark. */
    .ant-radio-button-wrapper:hover:not(.ant-radio-button-wrapper-checked):not(.ant-radio-button-wrapper-disabled) {
      background-color: var(--component-button-group-button-hover);
      color: var(--text-on-primary);
    }

    /* Cover :hover and :active too — on the solid path antd otherwise repaints the checked segment olive
         with white text (colorPrimaryHover / colorTextLightSolid); the extra pseudo-class out-weighs it. */
    .ant-radio-button-wrapper-checked,
    .ant-radio-button-wrapper-checked:hover,
    .ant-radio-button-wrapper-checked:active {
      background-color: var(--component-button-group-button-active);
      border-color: var(--component-button-group-border);
      /* Fixed black on the lime fill in both modes — the fill holds its brand colour. */
      color: var(--text-on-primary);
    }
    /* Grey label on every disabled segment; being last, it out-orders the checked black above for a
         selected-and-disabled segment. */
    .ant-radio-button-wrapper-disabled,
    .ant-radio-button-wrapper-disabled:hover {
      color: var(--text-on-disabled);
    }

    /* An unselected disabled segment has no fill; a selected disabled one keeps the lime fill (below) so
         it still reads as chosen — the VCS modal disables its only authentication option. */
    .ant-radio-button-wrapper-disabled:not(.ant-radio-button-wrapper-checked),
    .ant-radio-button-wrapper-disabled:not(.ant-radio-button-wrapper-checked):hover {
      background-color: transparent;
    }
    .ant-radio-button-wrapper-checked.ant-radio-button-wrapper-disabled,
    .ant-radio-button-wrapper-checked.ant-radio-button-wrapper-disabled:hover {
      background-color: var(--component-button-group-button-active);
    }
  }
`;

const Container = styled('div', {
  shouldForwardProp: prop => prop !== 'isHuge'
})<{ isHuge?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;

  /* Both variants start at the left edge: the huge cards under the field label they belong to
     (ENG-2791), the segmented control as one connected row with no gaps between segments. antd lays out
     the options-driven variant itself, so this only steers the buttons-prop path. */
  ${props =>
    props.isHuge
      ? css`
          justify-content: flex-start;
        `
      : css`
          flex-wrap: nowrap;
          justify-content: flex-start;
          gap: 0;
          width: fit-content;
        `}
`;

/**
 * The radius is `!important`: antd rounds only a group's end buttons, and these are standalone cards.
 *
 * The resting border is `border/primary`, the same neutral Card draws. It was `border/secondary` — the
 * lime — which painted every unselected card in the selected accent while the checked one rendered in
 * the bridge's darker green, so the whole group read as chosen (APO-475 browser pass).
 */
const HugeButton = styled(Radio.Button)`
  width: 205px;
  height: 80px;
  border-radius: 4px !important;
  border: var(--border-width-xs) solid var(--border-primary);
  /* Keep the label readable in every state. antd paints the checked and hover text with the lime
       colorPrimary, which is illegible on the white card — selection reads from the lime border, not the
       label. The doubled class out-weighs antd's own checked/hover text rule. */
  &&.ant-radio-button-wrapper-checked,
  &&.ant-radio-button-wrapper:hover:not(.ant-radio-button-wrapper-disabled) {
    color: var(--text-heading);
  }

  /* Card convention (card.tsx): hover moves the border, never a shadow. Only unselected cards — the
       selected one keeps its lime accent. */
  &&.ant-radio-button-wrapper:hover:not(.ant-radio-button-wrapper-checked):not(.ant-radio-button-wrapper-disabled) {
    border-color: var(--border-action);
  }
  padding: 0;
`;

export const HugeButtonContentContainer = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 1.3em;
  user-select: none;
`;

export default RadioButtons;
